import React, { cloneElement, isValidElement, useId } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

/**
 * FormField — label above any input, with helper or error text below
 * (UI Design Standards §4.2).
 *
 * The child input must NOT get a MUI `label` prop; that renders a second,
 * floating label. Pass `size="small"` to TextField / Select.
 *
 * The label is connected to the child through an `id` (generated unless the
 * child already has one). MUI Select also receives `labelId`. For inputs
 * whose id doesn't reach the <input> (e.g. date pickers), pass the id on the
 * rendered TextField yourself.
 *
 * Props:
 *   label       {node}           sentence case
 *   required    {bool}           appends " *"
 *   helperText  {node}
 *   error       {string|bool}    string replaces helperText; turns the border red
 *   touch       {bool}           44px min height (foreman-facing screens)
 *   children    single input element
 */
const FormField = ({
  label,
  required = false,
  helperText,
  error,
  touch = false,
  children,
  sx,
}) => {
  const theme = useTheme();
  const { ft } = theme.palette;
  const generatedId = useId();

  const child = isValidElement(children) ? children : null;
  const inputId = child?.props.id ?? `${generatedId}-input`;
  const labelId = `${inputId}-label`;
  const hasError = Boolean(error);
  const message = typeof error === 'string' && error ? error : helperText;

  const childProps = {};
  if (child && !child.props.id) childProps.id = inputId;
  if (child?.type?.muiName === 'Select' && !child.props.labelId) {
    childProps.labelId = labelId;
  }

  const borderColor = hasError ? ft.tones.error.text : ft.neutrals.fieldBorder;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        minWidth: 0,
        '& .MuiOutlinedInput-root': {
          backgroundColor: ft.neutrals.white,
          minHeight: touch ? 44 : 40,
          borderRadius: `${ft.radius.control}px`,
          fontSize: 14,
          color: ft.neutrals.textPrimary,
        },
        '& .MuiOutlinedInput-notchedOutline': { borderColor },
        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: hasError ? ft.tones.error.text : ft.neutrals.textDisabled,
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: hasError ? ft.tones.error.text : theme.palette.primary.main,
        },
        ...sx,
      }}
    >
      {label && (
        <Typography
          variant="fieldLabel"
          component="label"
          id={labelId}
          htmlFor={inputId}
          sx={{ color: ft.neutrals.textBody }}
        >
          {label}
          {required && ' *'}
        </Typography>
      )}
      {child ? cloneElement(child, childProps) : children}
      {message && (
        <Typography
          variant="caption"
          component="p"
          sx={{
            color: hasError ? ft.tones.error.text : ft.neutrals.textSecondary,
          }}
        >
          {message}
        </Typography>
      )}
    </Box>
  );
};

export default FormField;
